/**
 * The sky the BIM scene paints behind the model when the live-weather backdrop
 * is switched on.
 *
 * The scene's own backdrop is a 1-pixel-wide gradient (`useBimScene`'s
 * `paintGradient`), which is all a sky needed while the sun was one of six
 * presets. Once the sun follows the real clock and the weather is known, the
 * backdrop can be the sky the weather panels already draw: the same `skyAt()`
 * bands, the same condition table (`utils/weatherLook.ts`), the same
 * anti-solar moon. One sky, drawn twice — behind the hero, and behind the house.
 *
 * Split in two on purpose. `planBimSky` decides everything and returns a plain
 * object — colours, which bodies are up, where each cloud sits — so it can be
 * unit-tested in Node with no canvas. `paintBimSky` only executes canvas calls
 * against that plan. Every scatter (clouds, stars, rain) comes off a seeded
 * PRNG, so a repaint a minute later draws the *same* sky and only the things
 * that should move (the light, the sun's place) move. The rain and the snow are
 * the deliberate exception: they FALL, and `paintBimPrecipitation` is the layer
 * that is re-drawn per frame over a cached still sky (see `useBimScene`).
 *
 * The sun and the moon are NOT painted into this panel. They are real objects
 * out at the sun's own direction, in the scene (`useBimScene`'s sky discs), so
 * that they sit where the light and the sun arrow say they do and move with the
 * view. A disc painted onto a flat backdrop can only be right for one camera
 * pose, which is why the owner saw the arrow pointing one way and the painted
 * sun the other: *"the sun should move with the view to look like it's at the
 * right place"*. This panel draws what is flat and far: the gradient, the
 * clouds, the stars, the rain and the haze.
 */
import { skyAt } from './solarPosition';
import { CLEAR_LOOK, cloudStopsFor, drainSkyColors, weatherLookFor, type WeatherLook } from './weatherLook';

/**
 * Where the backdrop's horizon sits, as a fraction of its height.
 *
 * The backdrop's OWN horizon, not the weather panels' (`HORIZON_FRACTION`, 0.72
 * — their hill line, sun arc and dial are laid out against that number and must
 * not move with this one).
 *
 * Behind the model this has to sit ABOVE the terrain, or the sky's own horizon
 * band and everything under it are hidden by the model's own ground plane: at
 * 0.72, and even at 0.6, the visible backdrop was only the top of the sky. The
 * owner's *"horizont must be much higher"* is that. At 0.4 the sky's gradient
 * lives in the strip the terrain actually leaves visible.
 */
export const SKY_HORIZON = 0.4;

/** Where the sky's mid colour lands — three quarters of the way to the horizon. */
const MIDDLE_STOP = SKY_HORIZON * 0.75;

/** The disc's radius as a fraction of the sky's width — the hero's own discs. */
const SUN_RADIUS = 0.036;
const MOON_RADIUS = 0.028;
/** The sun's halo, as a fraction of the width (the hero's `inset: -52px`). */
const SUN_HALO_RADIUS = 0.14;
/** The moon's halo, as a fraction of the width (the hero's `inset: -24px`). */
const MOON_RADIUS_HALO = 0.075;

/**
 * The fraction of the viewport's WIDTH each body's sprite texture spans.
 *
 * The texture is the halo (the disc sits inside it at its own fraction), so this
 * is what keeps the sun and the moon the size they were when they were painted
 * into the backdrop: `useBimScene` sizes the sprite from this.
 */
export const SUN_DISC_EXTENT = SUN_HALO_RADIUS * 2;
export const MOON_DISC_EXTENT = MOON_RADIUS_HALO * 2;

/** The scatter's seed: fixed, so the sky is the same sky on every repaint. */
const DEFAULT_SEED = 20_261_015;

/** One body the hour puts in the sky, and how strongly. */
export interface BimSkyDisc {
  kind: 'sun' | 'moon';
  opacity: number;
  /** Moon phase 0–1 (0 = new, 0.5 = full); `null` for the sun. */
  phase: number | null;
}

/**
 * Where the sun is, as a direction from the model — the vector the light, the sun
 * arrow and the sky's own sun disc all share.
 *
 * The mirror in X is deliberate and long-standing (`useBimScene.applySunTime`):
 * east is `−X`, north is `+Z`, so a day's sweep runs from one to the other and
 * noon sits due south at `−Z` — which is what "the sun crosses the sky from left
 * to right" means once the sun is an object in the world rather than a mark on a
 * panel.
 */
export function sunDirectionAt(altitudeDeg: number, azimuthDeg: number): { x: number; y: number; z: number } {
  const elevation = (altitudeDeg * Math.PI) / 180;
  const azimuth = (azimuthDeg * Math.PI) / 180;
  return {
    x: -Math.cos(elevation) * Math.sin(azimuth),
    y: Math.sin(elevation),
    z: Math.cos(elevation) * Math.cos(azimuth),
  };
}

/** One cloud puff. `radiusFrac` is of the width; `xFrac`/`yFrac` of the sky. */
export interface BimCloudPuff {
  xFrac: number;
  yFrac: number;
  radiusFrac: number;
  opacity: number;
  color: string;
}

/** One star. */
export interface BimSkyStar {
  xFrac: number;
  yFrac: number;
  radiusFrac: number;
  opacity: number;
}

/** One falling drop (a rain streak) or flake (a snow dot). */
export interface BimSkyDrop {
  xFrac: number;
  /** Where the drop is at `seconds = 0`; the painter advances it from here. */
  yFrac: number;
  /** The streak's length, as a fraction of the width (snow uses it as a size). */
  lengthFrac: number;
  opacity: number;
  /** How fast it falls, as a fraction of the sky's height per second. */
  speedFrac: number;
}

export interface BimSkyPlan {
  /** The vertical gradient, top to bottom. */
  colors: { top: string; middle: string; horizon: string; bottom: string };
  /**
   * Which bodies the hour puts in the sky, and how strongly. WHERE they go is
   * the scene's business, not this panel's: they are real objects out at the
   * sun's direction (`useBimScene`'s sky discs), so that they sit where the light
   * and the sun arrow say they do and move with the view. Painting them into a
   * flat backdrop could only ever place them correctly for one camera pose — the
   * owner's *"the sun should move with the view to look like it's at the right
   * place"*.
   */
  discs: BimSkyDisc[];
  clouds: BimCloudPuff[];
  stars: BimSkyStar[];
  /**
   * `null` when nothing is falling. The drops carry their own place and speed,
   * so the backdrop can keep drawing them as they fall (`dropYFracAt`).
   */
  precipitation: { kind: 'drizzle' | 'rain' | 'snow'; drops: BimSkyDrop[]; opacity: number } | null;
  /** Horizontal haze bands at the horizon, and their opacity. */
  mist: { bands: number; opacity: number } | null;
  /** The dark film the hour wears, over the whole sky (0–1 alpha). */
  dim: number;
}

export interface BimSkyInput {
  /** The sun's altitude in degrees — negative when it is below the horizon. */
  altitudeDeg: number;
  /** The OpenWeatherMap code, or `null` when the hub has no weather to show. */
  condition: string | null;
  /** The moon's phase, 0–1 (0 = new, 0.5 = full); `null` draws no moon. */
  moonPhase: number | null;
  /** The themed ground colour the gradient ends on (`#rrggbb`). */
  bottomHex: string;
  /** Override for the scatter (tests); the same seed gives the same sky. */
  seed?: number;
}

/** Park–Miller: small, deterministic, and good enough for scattered puffs. */
function makeRandom(seed: number): () => number {
  let state = seed % 2_147_483_647;
  if (state <= 0) state += 2_147_483_646;
  return () => {
    state = (state * 48_271) % 2_147_483_647;
    return state / 2_147_483_647;
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * The bodies an hour and a condition put in the sky — the sun when it is up, and
 * the moon when it is not. Placement is the scene's (`useBimScene`), which knows
 * where the sun's own direction points.
 */
function discsFor(sky: { sun: number; stars: number }, look: WeatherLook, moonPhase: number | null): BimSkyDisc[] {
  const discs: BimSkyDisc[] = [];

  const sunOpacity = sky.sun * look.sunDim;
  if (sunOpacity > 0.01) {
    discs.push({ kind: 'sun', opacity: sunOpacity, phase: null });
  }

  // The moon rides the anti-solar point: the hub's moon position is only true
  // for "now", and a phase-correct moon in a plausible place reads better than
  // a blank night. (`WeatherSky` makes the same trade, and says so.)
  if (moonPhase !== null && sky.sun < 0.45) {
    discs.push({ kind: 'moon', opacity: 1 - look.cover * 0.7, phase: moonPhase });
  }

  return discs;
}

function cloudsFor(look: WeatherLook, sky: { sun: number }, random: () => number): BimCloudPuff[] {
  if (look.clouds <= 0) return [];
  const stops = cloudStopsFor(look);
  const colour = sky.sun < 0.5 ? stops.night : stops.day;
  // Four per grade: a clear sky keeps one puff low on the horizon, an overcast
  // covers the sky. `clouds` runs 1–4.
  const count = look.clouds * 4;
  return Array.from({ length: count }, (_, index) => {
    // Above the horizon, always: the puffs are the sky's own, and a cloud
    // hanging over the ground colour reads as a smudge on the terrain.
    const yFrac = (0.08 + random() * 0.74) * SKY_HORIZON;
    return {
      // Spread across the whole width, then nudge each puff so the field does
      // not read as a row of the same cloud.
      xFrac: (index + 0.5) / count + (random() - 0.5) * (0.9 / count),
      yFrac,
      radiusFrac: 0.06 + random() * 0.07,
      opacity: look.cloudOpacity * (0.5 + random() * 0.5),
      // Higher puffs catch the light, lower ones sit in the cloud's own shade.
      color: mixByLight(colour.top, colour.bottom, yFrac / SKY_HORIZON),
    };
  });
}

/** Blend two `#rrggbb` colours by hand — the plan may run outside the DOM. */
function mixByLight(top: string, bottom: string, t: number): string {
  const amount = clamp(t, 0, 1);
  const parse = (hex: string): number[] => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
  const a = parse(top);
  const b = parse(bottom);
  const mix = (i: number): string =>
    Math.round(a[i]! + (b[i]! - a[i]!) * amount)
      .toString(16)
      .padStart(2, '0');
  return `#${[0, 1, 2].map(mix).join('')}`;
}

function starsFor(sky: { stars: number }, look: WeatherLook, random: () => number): BimSkyStar[] {
  // Stars follow the cover: full under a clear night, gone under cloud and rain.
  const opacity = sky.stars * (1 - look.cover);
  if (opacity <= 0.01) return [];
  return Array.from({ length: 64 }, () => ({
    xFrac: random(),
    // Above the horizon, where the sky is.
    yFrac: random() * SKY_HORIZON * 0.9,
    // `radiusFrac` is a share of the width, as the discs' is.
    radiusFrac: 0.0007 + random() * 0.0011,
    opacity: (0.35 + random() * 0.65) * opacity,
  }));
}

/**
 * How fast a drop falls, as a fraction of the sky's height per second.
 *
 * Off the drop's own length: a long streak is a heavy drop and sweeps past, a
 * short one hangs — which is what gives the field depth instead of a single
 * curtain of equal drops. Snow sinks, drizzle drifts, rain falls.
 */
function fallSpeedFor(kind: 'drizzle' | 'rain' | 'snow', lengthFrac: number): number {
  if (kind === 'snow') return 0.05 + lengthFrac * 9;
  return (0.26 + lengthFrac * 8) * (kind === 'drizzle' ? 0.55 : 1);
}

function precipitationFor(look: WeatherLook, sky: { sun: number }, random: () => number): BimSkyPlan['precipitation'] {
  const kind = look.precipitation;
  if (kind === 'none') return null;
  const count = kind === 'drizzle' ? 26 : 48;
  const drops = Array.from({ length: count }, () => {
    const xFrac = random();
    // Where the drop is at `seconds = 0`: motion advances it from here, so a
    // sky that must hold still (reduced motion) still has its rain.
    const yFrac = random();
    const lengthFrac = kind === 'snow' ? 0.004 + random() * 0.003 : 0.012 + random() * 0.02;
    return {
      xFrac,
      yFrac,
      lengthFrac,
      opacity: kind === 'snow' ? 0.5 + random() * 0.4 : 0.25 + random() * 0.35,
      speedFrac: fallSpeedFor(kind, lengthFrac),
    };
  });
  // Rain is bright against a dark sky and dark against a bright one; snow reads
  // white either way. `sky.sun` is the hour's own light.
  return { kind, drops, opacity: sky.sun < 0.5 ? 0.85 : 0.7 };
}

/**
 * Everything the weather backdrop needs to draw, decided once.
 *
 * Pure: no canvas, no clock, no globals. The same input always gives the same
 * plan, which is what lets the painted sky hold still between repaints.
 */
export function planBimSky(input: BimSkyInput): BimSkyPlan {
  const sky = skyAt(input.altitudeDeg);
  const look = input.condition === null ? CLEAR_LOOK : weatherLookFor(input.condition);
  const nightCover = (1 - sky.sun) * look.cover;
  // Drawn in the order the scatter is consumed, so every seeded series stays
  // put when a later one changes.
  const random = makeRandom(input.seed ?? DEFAULT_SEED);

  const clouds = cloudsFor(look, sky, random);
  const stars = starsFor(sky, look, random);
  const precipitation = precipitationFor(look, sky, random);

  return {
    colors: { ...drainSkyColors(sky, look, nightCover), bottom: input.bottomHex },
    discs: discsFor(sky, look, input.moonPhase),
    clouds,
    stars,
    precipitation,
    mist: look.mist ? { bands: 4, opacity: 0.16 } : null,
    // At night the cover itself already carries the mood (the overcast greys),
    // so the film thins instead of stacking a second black on top of it.
    dim: look.dim * (1 - nightCover * 0.75),
  };
}

/**
 * Paint a plan onto a canvas context, at `width` × `height`.
 *
 * Every scatter coordinate in the plan is a fraction, so the same plan paints
 * correctly at any size — the backdrop is re-sized to the viewport's own aspect
 * (a background texture is stretched across it, so a canvas of the wrong shape
 * would stretch the sun into an ellipse).
 *
 * `seconds` is how far into the fall the precipitation is drawn; the default
 * paints the plan's own still frame.
 */
export function paintBimSky(
  ctx: CanvasRenderingContext2D,
  plan: BimSkyPlan,
  width: number,
  height: number,
  seconds = 0,
): void {
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, plan.colors.top);
  gradient.addColorStop(MIDDLE_STOP, plan.colors.middle);
  gradient.addColorStop(SKY_HORIZON, plan.colors.horizon);
  gradient.addColorStop(1, plan.colors.bottom);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  // Everything in the sky is drawn ONCE. It is tempting to draw the scatter a
  // second time shifted a full width, so a cloud over the right edge reappears at
  // the left and the backdrop could pan with the camera — but this canvas is a
  // 180° panel, not a 360° panorama, and three renders a background texture as a
  // stretched quad. Wrapping it therefore does not scroll the sky; it FABRICATES
  // sky, and the sun's copy shows up at the opposite edge (the owner, seeing
  // exactly that: *"i see two suns now"*). A sky that panning needs a real dome
  // mesh in the scene, not a wrapped texture.
  paintSkyScatter(ctx, plan, width, height, seconds);

  if (plan.mist) {
    // The haze sits ON the horizon line and just under it, which is what a
    // horizon is for — so the bands follow `SKY_HORIZON` rather than a fixed row.
    // It is a full-width band, so it needs no wrap.
    for (let band = 1; band <= plan.mist.bands; band += 1) {
      const y = SKY_HORIZON * (0.85 + band * 0.08) * height;
      const bandGradient = ctx.createLinearGradient(0, y - height * 0.035, 0, y + height * 0.035);
      bandGradient.addColorStop(0, 'rgba(226, 232, 240, 0)');
      bandGradient.addColorStop(0.5, `rgba(226, 232, 240, ${plan.mist.opacity.toFixed(3)})`);
      bandGradient.addColorStop(1, 'rgba(226, 232, 240, 0)');
      ctx.fillStyle = bandGradient;
      ctx.fillRect(0, y - height * 0.035, width, height * 0.07);
    }
  }

  paintBimDim(ctx, plan.dim, width, height);
}

/**
 * The dark film the hour wears, over the whole sky (0–1 alpha).
 *
 * Its own function because the falling drops are re-drawn over a cached still
 * sky, and the film sits over them too — a repaint must not skip it, or the rain
 * turns bright the moment it starts moving at night.
 */
export function paintBimDim(ctx: CanvasRenderingContext2D, dim: number, width: number, height: number): void {
  if (dim <= 0) return;
  ctx.fillStyle = `rgba(0, 0, 0, ${dim.toFixed(3)})`;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Where a drop is `seconds` into its fall: the plan's own place advanced by the
 * drop's speed, wrapped around the sky.
 *
 * Pure, so the motion is pinned by a test rather than by eye: the field is the
 * same field on every frame — only the drops have moved.
 */
export function dropYFracAt(drop: BimSkyDrop, seconds: number): number {
  const fallen = drop.yFrac + drop.speedFrac * seconds;
  return fallen - Math.floor(fallen);
}

/** Everything in the sky that has a place: stars, clouds, discs, precipitation. */
function paintSkyScatter(
  ctx: CanvasRenderingContext2D,
  plan: BimSkyPlan,
  width: number,
  height: number,
  seconds: number,
): void {
  for (const star of plan.stars) {
    ctx.beginPath();
    ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity.toFixed(3)})`;
    ctx.arc(star.xFrac * width, star.yFrac * height, Math.max(star.radiusFrac * width, 0.6), 0, Math.PI * 2);
    ctx.fill();
  }

  for (const puff of plan.clouds) {
    const radius = puff.radiusFrac * width;
    const x = puff.xFrac * width;
    const y = puff.yFrac * height;
    const puffGradient = ctx.createRadialGradient(x, y, radius * 0.15, x, y, radius);
    puffGradient.addColorStop(0, withAlpha(puff.color, puff.opacity));
    puffGradient.addColorStop(0.55, withAlpha(puff.color, puff.opacity * 0.55));
    puffGradient.addColorStop(1, withAlpha(puff.color, 0));
    ctx.fillStyle = puffGradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  if (plan.precipitation) paintBimPrecipitation(ctx, plan.precipitation, width, height, seconds);
}

/**
 * Everything that falls, drawn at `seconds` into the fall.
 *
 * Its own function because the rest of the sky is painted once and left alone:
 * `useBimScene` keeps the still sky cached and re-draws THIS layer over it per
 * animation frame, so falling rain costs a blit and a few strokes rather than
 * the whole gradient-and-clouds picture. `seconds = 0` is the plan's own still
 * frame — what a sky that must not move keeps.
 */
export function paintBimPrecipitation(
  ctx: CanvasRenderingContext2D,
  precipitation: NonNullable<BimSkyPlan['precipitation']>,
  width: number,
  height: number,
  seconds = 0,
): void {
  const { kind, drops, opacity } = precipitation;
  if (kind === 'snow') {
    for (const drop of drops) {
      // A flake drifts as it sinks: a dot falling on a plumb line reads as a
      // hole in the sky, not as snow.
      const sway = Math.sin(seconds * 0.9 + drop.xFrac * 40) * 0.004;
      ctx.beginPath();
      ctx.fillStyle = `rgba(255, 255, 255, ${(drop.opacity * opacity).toFixed(3)})`;
      ctx.arc(
        (drop.xFrac + sway) * width,
        dropYFracAt(drop, seconds) * height,
        Math.max(drop.lengthFrac * width, 0.8),
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
    return;
  }

  ctx.lineWidth = kind === 'drizzle' ? 1 : 1.5;
  ctx.lineCap = 'round';
  for (const drop of drops) {
    const x = drop.xFrac * width;
    const y = dropYFracAt(drop, seconds) * height;
    const length = drop.lengthFrac * width;
    ctx.beginPath();
    ctx.strokeStyle = `rgba(219, 234, 254, ${(drop.opacity * opacity).toFixed(3)})`;
    // A slight lean, so the fall reads as wind-blown rather than as a grid.
    ctx.moveTo(x, y);
    ctx.lineTo(x - length * 0.18, y + length);
    ctx.stroke();
  }
}

/**
 * The sun, drawn into a square canvas of `size` px: a soft halo and the disc.
 *
 * Standalone (not part of `paintBimSky`) because the sun is a real object in the
 * scene now — this is the texture of its sprite (`useBimScene`'s sky discs), so
 * it is the same art wherever it is drawn.
 */
export function paintSunDisc(ctx: CanvasRenderingContext2D, size: number, opacity: number): void {
  const centre = size / 2;
  const radius = size * 0.5 * (SUN_RADIUS / SUN_HALO_RADIUS);

  const halo = ctx.createRadialGradient(centre, centre, radius * 0.4, centre, centre, size * 0.5);
  halo.addColorStop(0, `rgba(255, 236, 150, ${(0.5 * opacity).toFixed(3)})`);
  halo.addColorStop(0.45, `rgba(255, 212, 90, ${(0.22 * opacity).toFixed(3)})`);
  halo.addColorStop(1, 'rgba(255, 190, 60, 0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, size, size);

  const body = ctx.createRadialGradient(
    centre - radius * 0.2,
    centre - radius * 0.32,
    radius * 0.1,
    centre,
    centre,
    radius,
  );
  body.addColorStop(0, withAlpha('#fff6c9', opacity));
  body.addColorStop(0.48, withAlpha('#ffdd6b', opacity));
  body.addColorStop(1, withAlpha('#ffb020', opacity));
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(centre, centre, radius, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * The moon, drawn into a square canvas of `size` px at a phase — the sprite's
 * texture, so it reuses the same halo and the same terminator shape the panel
 * would have drawn.
 */
export function paintMoon(ctx: CanvasRenderingContext2D, size: number, phase: number, opacity: number): void {
  const centre = size / 2;
  const radius = size * 0.5 * (MOON_RADIUS / MOON_RADIUS_HALO);

  const halo = ctx.createRadialGradient(centre, centre, radius * 0.5, centre, centre, size * 0.5);
  halo.addColorStop(0, `rgba(214, 228, 255, ${(0.34 * opacity).toFixed(3)})`);
  halo.addColorStop(1, 'rgba(180, 200, 240, 0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, size, size);

  paintMoonDisc(ctx, centre, centre, radius, phase, opacity);
}

/** `#rrggbb` at an alpha, as an `rgba()` the canvas accepts. */
function withAlpha(hex: string, alpha: number): string {
  return `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(
    hex.slice(5, 7),
    16,
  )}, ${clamp(alpha, 0, 1).toFixed(3)})`;
}

/**
 * The lit part of a moon at a phase, as one closed path.
 *
 * The lit region is bounded by the disc's limb on one side and by the
 * terminator — an ellipse whose half-width is `r·cos(2π·phase)` — on the other.
 * Waxing lights the right side (phase < 0.5), waning the left; the terminator's
 * own sign says which way it bulges, which is what separates a crescent from a
 * gibbous shape at the same illumination.
 */
function paintMoonDisc(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  phase: number,
  opacity: number,
): void {
  const illumination = (1 - Math.cos(2 * Math.PI * phase)) / 2;
  if (illumination <= 0.01) return; // new moon: there is nothing to light

  const waxing = phase < 0.5;
  const signed = (waxing ? 1 : -1) * Math.cos(2 * Math.PI * phase);

  const body = ctx.createRadialGradient(x - radius * 0.3, y - radius * 0.3, radius * 0.1, x, y, radius);
  body.addColorStop(0, withAlpha('#ffffff', opacity));
  body.addColorStop(0.45, withAlpha('#eaeff6', opacity));
  body.addColorStop(1, withAlpha('#8b99ac', opacity));
  ctx.fillStyle = body;

  ctx.beginPath();
  // The lit limb: the right half when waxing, the left when waning.
  ctx.arc(x, y, radius, -Math.PI / 2, Math.PI / 2, !waxing);
  // …then the terminator back up to the top. `counterclockwise` (in canvas
  // angles, which run clockwise on screen because y points down) is what swings
  // the bulge to the lit side for a crescent and away from it for a gibbous.
  ctx.ellipse(x, y, Math.abs(signed) * radius, radius, 0, Math.PI / 2, -Math.PI / 2, signed > 0);
  ctx.closePath();
  ctx.fill();
}
